'use client';

import { IconButton } from '../../components/icon-button';
import { Skeleton } from '../../components/skeleton';
import { BodyDefault } from '../../components/typography';
import { automationId } from '@terra/shared-utils';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { ArrowLeft02Icon } from '@terra/ui-icons';
import {
  SegmentGroupHorizontal,
  type SegmentGroupHorizontalProps,
} from '../../components/segment-group-horizontal';
import { Breadcrumbs, type BreadcrumbItem } from './Breadcrumbs';
import { PageHeaderTitle } from './PageHeaderTitle';

const TITLE_SLOT_WIDTH = '10rem';

export type PageHeaderProps = {
  title?: React.ReactNode;
  titleTestId?: string;
  isTitleLoading?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  subtitle?: React.ReactNode;
  backNavigation?: {
    onBack: () => void;
    'aria-label'?: string;
    dataTestid?: string;
  };
  titleIcon?: React.ReactNode;
  action?: {
    icon: React.ReactNode;
    onClick?: () => void;
    'aria-label'?: string;
    disabled?: boolean;
  };
  buttons?: React.ReactNode[];
  segmentTabs?: SegmentGroupHorizontalProps;
};
export const PageHeader = ({
  title,
  titleTestId,
  isTitleLoading,
  breadcrumbs,
  subtitle,
  backNavigation,
  action,
  buttons = [],
  segmentTabs,
}: PageHeaderProps) => {
  const shouldReserveTitleSlot = isTitleLoading !== undefined;
  const rightContent =
    buttons.length > 0 ? (
      <HStack alignItems="center" gap={2} flexShrink={0}>
        {buttons.map((button, index) => (
          <Box key={index}>{button}</Box>
        ))}
      </HStack>
    ) : null;

  return (
    <Box w="full">
      <HStack
        justify="space-between"
        alignItems="center"
        w="full"
        gap={4}
        minW={0}
      >
        <HStack alignItems="center" gap={2} flex={1} minW={0}>
          {backNavigation && (
            <Box display="flex" alignItems="center" color="fg.default">
              <IconButton
                size="sm"
                variant="link"
                onClick={backNavigation.onBack}
                aria-label={backNavigation['aria-label'] || 'Go back'}
                {...automationId(backNavigation.dataTestid ?? '')}
                px={0}
                py={1.5}
              >
                <ArrowLeft02Icon size={18} />
              </IconButton>
            </Box>
          )}{' '}
          <VStack alignItems="flex-start" gap={0} flex={1} minW={0}>
            <HStack
              alignItems="center"
              gap={2}
              flexWrap="nowrap"
              minW={0}
              w="full"
            >
              {breadcrumbs && breadcrumbs.length > 0 ? (
                <Box flex={1} minW={0}>
                  <Breadcrumbs items={breadcrumbs} />
                </Box>
              ) : title !== undefined || isTitleLoading ? (
                <Box
                  w={shouldReserveTitleSlot ? TITLE_SLOT_WIDTH : 'auto'}
                  minW={shouldReserveTitleSlot ? TITLE_SLOT_WIDTH : 0}
                  maxW={shouldReserveTitleSlot ? TITLE_SLOT_WIDTH : 'full'}
                  flexShrink={shouldReserveTitleSlot ? 0 : 1}
                  minH="1.5em"
                  display="flex"
                  alignItems="center"
                >
                  {isTitleLoading ? (
                    <Skeleton h="1em" w="full" borderRadius="md" />
                  ) : (
                    <PageHeaderTitle title={title} titleTestId={titleTestId} />
                  )}
                </Box>
              ) : null}
              {action && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  w="8"
                  h="8"
                >
                  {action.onClick ? (
                    <Box color="fg.muted" display="flex" alignItems="center">
                      <IconButton
                        size="sm"
                        variant="link"
                        onClick={action.onClick}
                        disabled={action.disabled}
                        aria-label={action['aria-label']}
                      >
                        {action.icon}
                      </IconButton>
                    </Box>
                  ) : (
                    action.icon
                  )}
                </Box>
              )}
              {segmentTabs && (
                <Box
                  display="flex"
                  alignItems="center"
                  flex="1 1 0"
                  minW={0}
                  overflow="hidden"
                >
                  <SegmentGroupHorizontal {...segmentTabs} />
                </Box>
              )}
            </HStack>
            {typeof subtitle === 'string' ? (
              <BodyDefault>{subtitle}</BodyDefault>
            ) : (
              subtitle
            )}
          </VStack>
        </HStack>

        {rightContent}
      </HStack>
    </Box>
  );
};
